import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Box,
  Checkbox,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SortingState } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classSet from 'react-classset';
import { useFormContext as useOptionalFormContext } from 'react-hook-form';

import paginationUtils from '@/utils/paginationUtils';

import TableHeaderCell from './TableHeaderCell';
import type { TableToolBarAction } from './TableToolbar';
import TableToolbar from './TableToolbar';
import VNTableRow from './VNTableRow';

const DEFAULT_PAGINATION = {
  sortBy: 'createdDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

interface IProps {
  columns: any[];
  data?: any[]; // New optional prop
  showRowSelection?: boolean;
  paginationChange?: (pagination: any) => void;
  hidePagination?: boolean;
  loading?: boolean;
  loadingRows?: number;
  showActionbar?: boolean;
  handleActionClick?: (key: string) => void;
  toolBarActions?: TableToolBarAction[];
  toolBarbulkActions?: TableToolBarAction[];
  dataKey?: string; // Key for React Hook Form data
  selectedRowId?: string;
  sortable?: (prevItem: any, newItem: any, prevId: string, newId: string) => void;
  rowRefs?: any;
}

const VNTable: React.FC<IProps> = ({
  columns,
  data: externalData, // New prop
  showRowSelection = false,
  paginationChange,
  hidePagination = false,
  loading = false,
  loadingRows = 20,
  showActionbar = false,
  handleActionClick,
  toolBarActions = [],
  toolBarbulkActions = [],
  dataKey = 'data',
  selectedRowId = '',
  sortable,
  rowRefs = [],
}) => {
  const methods = useOptionalFormContext(); // Get form context if available
  const { watch, setValue } = methods || {};
  const watchedSelectedRows = watch ? watch('selectedRows') : [];
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllIndeterminate, setSelectAllIndeterminate] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const data = externalData ?? (watch?.(dataKey) || []);
  const paginationDetail = watch?.('pagination') || DEFAULT_PAGINATION;

  const safePaginationDetail = useMemo(() => paginationDetail || DEFAULT_PAGINATION, [paginationDetail]);

  const [pagination, setPagination] = useState({
    pageIndex: safePaginationDetail.page,
    pageSize: safePaginationDetail.rowsPerPage,
  });

  const paginationSummary = useMemo(
    () => paginationUtils.getPaginationSummary(safePaginationDetail, safePaginationDetail.totalItems),
    [safePaginationDetail]
  );

  const [sorting, setSorting] = useState<SortingState>(() => {
    const found = columns.find((c) => c.accessorKey === safePaginationDetail.sortBy);
    return found
      ? [
        {
          id: safePaginationDetail.sortBy,
          desc: safePaginationDetail.descending,
        },
      ]
      : [];
  });

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    defaultColumn: {
      size: 'auto',
    },
  });
  const { rows } = table.getRowModel();

  useEffect(() => {
    // console.log('data changed', watchedSelectedRows);
    if (data == null || data?.length === 0) {
      if (setValue) {
        setValue('selectedRows', []);
      }
    }
  }, [data]);

  useEffect(() => {
    // console.log('watchedSelectedRows', watchedSelectedRows);
    if (data?.length > 0 && watchedSelectedRows?.length > 0) {
      if (watchedSelectedRows.length === data?.length) {
        setSelectAll(true);
        setSelectAllIndeterminate(false);
      } else {
        setSelectAll(false);
        setSelectAllIndeterminate(true);
      }
    } else {
      setSelectAll(false);
      setSelectAllIndeterminate(false);
    }
  }, [watchedSelectedRows]);

  const handlePageChange = useCallback(
    (_: unknown, value: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: value }));
      paginationChange?.({ ...safePaginationDetail, page: value });
      if (setValue) {
        setValue('selectedRows', []);
      }
    },
    [safePaginationDetail, paginationChange]
  );

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

  const handleRequestSort = useCallback(
    (column: any) => {
      if (!column.columnDef?.meta?.sortable) return;

      const isDesc = safePaginationDetail.sortBy === column.id ? !safePaginationDetail.descending : false;
      setSorting([{ id: column.id, desc: isDesc }]);
      setPagination((prev) => ({ ...prev, pageIndex: 1 }));
      paginationChange?.({
        ...safePaginationDetail,
        sortBy: column.id,
        descending: isDesc,
        page: 1,
      });
    },
    [safePaginationDetail, paginationChange]
  );

  const handleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const allRows = table.getRowModel().rows;
    const newSelection = newSelectAll ? Object.fromEntries(allRows.map((row) => [row.id, true])) : {};
    setRowSelection(newSelection);
    if (setValue) {
      const selectedOriginalRows = newSelectAll ? allRows.map((row) => row.original) : [];
      if (setValue) {
        setValue('selectedRows', selectedOriginalRows);
      }
    }
  }, [selectAll]);

  const handleRowSelect = useCallback(
    (rowId: string) => {
      setRowSelection((prev) => {
        const updatedRowSelection = {
          ...prev,
          [rowId]: !prev[rowId],
        };
        const selectedOriginalRows = table
          .getRowModel()
          .rows.filter((row) => updatedRowSelection[row.id])
          .map((row) => row.original);

        if (setValue) {
          setValue('selectedRows', selectedOriginalRows);
        }
        return updatedRowSelection;
      });
    },
    [setRowSelection]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.active?.id || !event.over?.id) {
      return;
    }

    sortable?.(
      table.getRowModel().rows[event.active.id]?.original,
      table.getRowModel().rows[event.over.id]?.original,
      event.active.id,
      event.over.id
    );
  };

  const onActionClick = useCallback(
    async (key: string) => {
      await handleActionClick?.(key);
      setRowSelection({});
    },
    [handleActionClick]
  );

  return (
    <div className="vn-table">
      {showActionbar && <TableToolbar onActionClick={onActionClick} actions={toolBarActions} bulkActions={toolBarbulkActions} />}
      <div
        className={classSet({
          'table-wrapper': true,
          'with-pagination': !hidePagination,
          'with-header': showActionbar,
        })}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <TableContainer component={Paper} className="table-container">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {Boolean(sortable) && <TableCell className="header-cell sortable-cell" />}
                  {showRowSelection && (
                    <TableCell className="header-cell checkbox-cell">
                      <Checkbox
                        checked={selectAll}
                        indeterminate={selectAllIndeterminate}
                        onChange={handleSelectAll}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {table
                    .getHeaderGroups()
                    .map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <TableHeaderCell
                          key={header.id}
                          header={header}
                          paginationDetail={safePaginationDetail}
                          onRequestSort={handleRequestSort}
                        />
                      ))
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Show skeleton loader while loading
                  Array.from({ length: loadingRows }).map((_, index) => (
                    <TableRow key={index}>
                      {Boolean(sortable) && (
                        <TableCell className="sort-cell">
                          <Skeleton variant="rectangular" width={24} height={24} />
                        </TableCell>
                      )}
                      {showRowSelection && (
                        <TableCell className="checkbox-cell">
                          <Skeleton variant="rectangular" width={24} height={24} />
                        </TableCell>
                      )}
                      {columns.map((column, colIndex) => (
                        <TableCell key={colIndex}>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <>
                    {rows.length > 0 && (
                      <SortableContext items={rows.map((i) => i.id)} strategy={verticalListSortingStrategy} disabled={!sortable}>
                        {rows.map((row) => {
                          return (
                            <VNTableRow
                              key={row.id}
                              row={row}
                              selectedRowId={selectedRowId}
                              showRowSelection={showRowSelection}
                              rowSelection={rowSelection}
                              handleRowSelect={handleRowSelect}
                              sortable={Boolean(sortable)}
                              rowRefs={rowRefs}
                            />
                          );
                        })}
                      </SortableContext>
                    )}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan="100%" className="empty-cell">
                          <Typography sx={{ textAlign: 'center' }}>No data available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DndContext>
      </div>
      {!hidePagination && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography>
            Showing {paginationSummary.from} - {paginationSummary.to} of {paginationSummary.total}
          </Typography>
          {!loading && (
            <Pagination
              size="small"
              count={safePaginationDetail.pages}
              page={safePaginationDetail.page}
              variant="outlined"
              shape="rounded"
              color="primary"
              onChange={handlePageChange}
            />
          )}
        </Box>
      )}
    </div>
  );
};

export default React.memo(VNTable);
